/**
 * Sales Data Hooks
 *
 * Custom React Query hooks for fetching sales analytics data.
 * Provides automatic caching, loading states, and error handling.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { SalesAnalyticsService } from "../services/sales-analytics";
import type {
  SalesOverview,
  SalespersonDetails,
  CustomerDetailData,
  TopProjects,
  MonthlyTrendData,
  TopCustomersData
} from "@/shared/types/sales-analytics";

/**
 * Hook to fetch sales overview data
 *
 * @param params - Optional period, comparison flags, and preview parameters
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading, error } = useSalesOverview({
 *   period: "2025-09",
 *   includeComparison: true,
 *   previewAs: "salespersonId" // Admin preview mode
 * });
 */
export function useSalesOverview(
  params?: {
    period?: string;
    includeComparison?: boolean;
    previewAs?: string; // Admin preview mode: view as specific salesperson
  },
  options?: Omit<UseQueryOptions<SalesOverview>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<SalesOverview>({
    queryKey: ['salesOverview', params],
    queryFn: () => service.getSalesOverview(params),
    ...options
  });
}

/**
 * Hook to fetch salesperson details with analytics
 *
 * @param salespersonId - Salesperson gUId
 * @param params - Optional period parameter
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading } = useSalespersonDetails("SP-001", {
 *   period: "2025-09"
 * });
 */
export function useSalespersonDetails(
  salespersonId: string | undefined,
  params?: {
    period?: string;
  },
  options?: Omit<UseQueryOptions<SalespersonDetails>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<SalespersonDetails>({
    queryKey: ['salespersonDetails', salespersonId, params],
    queryFn: () => {
      if (!salespersonId) {
        throw new Error("Salesperson ID is required");
      }
      return service.getSalespersonDetails(salespersonId, params);
    },
    enabled: !!salespersonId && (options?.enabled !== false),
    ...options
  });
}

/**
 * Hook to fetch top projects by revenue
 *
 * @param params - Optional period and limit
 * @param options - React Query options
 *
 * @example
 * const { data } = useTopProjects({
 *   period: "2025-09",
 *   limit: 20
 * });
 */
export function useTopProjects(
  params?: {
    period?: string;
    limit?: 5 | 10 | 20 | 50;
    previewAs?: string;
  },
  options?: Omit<UseQueryOptions<TopProjects>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<TopProjects>({
    queryKey: ['topProjects', params],
    queryFn: () => service.getTopProjects(params),
    ...options
  });
}

/**
 * Helper hook to get current period in YYYY-MM format
 */
export function useCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed

  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Helper hook to format period for display
 */
export function useFormatPeriod(period?: string) {
  if (!period) return null;

  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return null;

  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}

/**
 * Hook to fetch monthly sales trend data
 *
 * @param year - Year for trend data (defaults to current year)
 * @param metric - Metric type: 'revenue' (paid amounts) or 'sales' (invoice amounts)
 * @param options - React Query options
 *
 * @example
 * const { data } = useMonthlySalesTrend(2025, 'revenue');
 */
export function useMonthlySalesTrend(
  year?: number,
  metric?: 'revenue' | 'sales',
  previewAs?: string,
  options?: Omit<UseQueryOptions<MonthlyTrendData>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  const currentYear = year || new Date().getFullYear();
  const metricType = metric || 'revenue';

  return useQuery<MonthlyTrendData>({
    queryKey: ['monthlySalesTrend', currentYear, metricType, previewAs],
    queryFn: () => service.getMonthlySalesTrend(currentYear, metricType, previewAs),
    ...options
  });
}

/**
 * Hook to fetch global top spending customers
 *
 * @param params - Optional period and limit
 * @param options - React Query options
 *
 * @example
 * const { data } = useTopCustomersGlobal({
 *   period: "2025-09",
 *   limit: 10
 * });
 */
export function useTopCustomersGlobal(
  params?: {
    period?: string;
    limit?: number;
    previewAs?: string;
  },
  options?: Omit<UseQueryOptions<TopCustomersData>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<TopCustomersData>({
    queryKey: ['topCustomersGlobal', params],
    queryFn: () => service.getTopCustomers(params),
    ...options
  });
}

/**
 * Hook to fetch customer details with analytics
 *
 * @param customerId - Customer code
 * @param params - Optional period and trend flags
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading } = useCustomerDetails("CUST-001", {
 *   period: "2025-09",
 *   includeMonthlyTrend: true
 * });
 */
export function useCustomerDetails(
  customerId: string | undefined,
  params?: {
    period?: string;
    includeMonthlyTrend?: boolean;
  },
  options?: Omit<UseQueryOptions<CustomerDetailData>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<CustomerDetailData>({
    queryKey: ['customerDetails', customerId, params],
    queryFn: () => {
      if (!customerId) {
        throw new Error("Customer ID is required");
      }
      return service.getCustomerDetails(customerId, params);
    },
    enabled: !!customerId && (options?.enabled !== false),
    ...options
  });
}
